/* eslint-disable react/prop-types */
import { useState, useEffect, Suspense, useCallback } from "react";
import { ThemeFormSubHeader } from "../../../../../shared/themeFormSubHeader";
import initCapitalize from "../../../../../../utils/commonFunctions/initCapitalize";
import ProfileEditDialog from "../../../../profile/profileDetails/profileEditDialog/ProfileEditDialog";
import DynamicInformationForm from "../../../../../shared/dynamicInformationForm/DynamicInformationForm";
import prefixCharacterAddition from "../../../../../../utils/commonFunctions/prefixCharacterAddition";
import { dateTimeZoneConverter } from "../../../../../../utils/commonFunctions/date";

const DependentInformation = ({ dependentDetails, setDependentDetails }) => {
	const { ZIP, gender, dependent_status, SSN } = dependentDetails;
	// console.log("ZIP", ZIP);
	// console.log("Gender", gender);
	const [dependentProfileInformation, setDependentProfileInformation] = useState([]);
	const [dependentContactInformation, setDependentContactInformation] = useState([]);
	const [editProfileDialogProps, setEditProfileDialogProps] = useState({
		openDialog: false,
		actions: [
			{
				label: "Cancel",
				name: "cancel",
				callback: () => {
					setEditProfileDialogProps(Object.assign({}, editProfileDialogProps, { openDialog: false }));
				}
			}
		]
	});

	const getDepedentProfileInformation = useCallback(() => {
		setDependentProfileInformation(
			Object.assign(
				[],
				[
					{
						label: "Dependent Name",
						name: "dependent_name",
						value: [
							dependentDetails?.first_name,
							dependentDetails?.middle_name,
							dependentDetails?.last_name
						]
							.filter(Boolean) // Filter out any empty or undefined values
							.join(" "),
						type: "textfield"
					},
					{
						label: "SSN",
						name: "SSN",
						value:
							dependentDetails.SSN.substring(0, 3) +
							"-" +
							dependentDetails.SSN.substring(3, 5) +
							"-" +
							dependentDetails.SSN.substring(5),
						type: "textfield"
					},
					{
						label: "Date of Birth",
						name: "date_of_birth",
						value: dateTimeZoneConverter(dependentDetails?.date_of_birth),
						type: "textfield"
					},
					{
						label: "Gender",
						name: "gender",
						value: initCapitalize(gender),
						type: "textfield"
					},
					{
						label: "Relationship",
						name: "relationship",
						value:
							dependentDetails?.relationship !== null
								? initCapitalize(dependentDetails?.relationship)
								: dependentDetails?.relationship,
						type: "textfield"
					},
					{
						label: "Dependent Type",
						name: "dependent_type",
						value: dependentDetails?.dependent_type,
						type: "textfield"
					},

					{
						label: "Dependent Status",
						name: "dependent_status",
						value: dependent_status ? initCapitalize(dependent_status) : dependent_status,
						type: "textfield"
					}
				]
			)
		);
	}, [
		dependentDetails.SSN,
		dependentDetails?.dependent_type,
		dependentDetails?.first_name,
		dependentDetails?.last_name,
		dependentDetails?.middle_name,
		dependentDetails?.relationship,
		dependent_status,
		gender
	]);

	const getDependentContactInformation = useCallback(() => {
		setDependentContactInformation(
			Object.assign(
				[],
				[
					{
						label: "Email",
						name: "email",
						value: dependentDetails.email,
						type: "textfield"
					},
					{
						label: "Contact Label",
						name: "contact_label",
						value: dependentDetails?.contact_label
							? initCapitalize(dependentDetails?.contact_label)
							: dependentDetails?.contact_label,
						type: "textfield"
					},
					{
						label: "Phone Extension",
						name: "phone_extension",
						value: dependentDetails.phone_extension,
						type: "textfield"
					},
					{
						label: "Phone Number",
						name: "phone_number",
						value: dependentDetails.phone_number,
						type: "textfield"
					},
					{
						label: "Address Line 1",
						name: "address_line_1",
						value: dependentDetails?.address_line_1,
						type: "textfield"
					},
					{
						label: "Address Line 2",
						name: "address_line_2",
						value: dependentDetails?.address_line_2,
						type: "textfield"
					},
					{
						label: "City",
						name: "city",
						value: dependentDetails?.city,
						type: "textfield"
					},
					{
						label: "State",
						name: "state",
						value: dependentDetails.state,
						type: "textfield"
					},
					{
						label: "Country",
						name: "country",
						value: dependentDetails?.country,
						type: "textfield"
					},
					{
						label: "ZIP Code",
						name: "ZIP",
						value:
							String(dependentDetails.ZIP).length > 5
								? dependentDetails?.ZIP?.substring(0, 5) + "-" + dependentDetails?.ZIP?.substring(5)
								: dependentDetails?.ZIP,
						type: "textfield"
					}
				]
			)
		);
	}, [
		dependentDetails.ZIP,
		dependentDetails?.address_line_1,
		dependentDetails?.address_line_2,
		dependentDetails?.city,
		dependentDetails?.contact_label,
		dependentDetails?.country,
		dependentDetails.email,
		dependentDetails.phone_extension,
		dependentDetails.phone_number,
		dependentDetails.state
	]);

	const handleOpenEditProfileDialogClick = useCallback(() => {
		setEditProfileDialogProps(Object.assign({}, editProfileDialogProps, { openDialog: true }));
	}, [editProfileDialogProps]);

	const handleEditProfileDialogClose = useCallback(() => {
		setEditProfileDialogProps(Object.assign({}, editProfileDialogProps, { openDialog: false }));
	}, [editProfileDialogProps]);

	useEffect(() => {
		getDepedentProfileInformation();
		getDependentContactInformation();
	}, [getDepedentProfileInformation, getDependentContactInformation]);

	return (
		<>
			<Suspense fallback={<div />}>
				<ThemeFormSubHeader
					title={"Profile Information"}
					actionButtons={[
						{
							title: "Edit Request",
							callback: handleOpenEditProfileDialogClick
						}
					]}
				/>
			</Suspense>
			<Suspense fallback={<div />}>
				<ProfileEditDialog
					dialogProps={editProfileDialogProps}
					handleClose={handleEditProfileDialogClose}
					member={dependentDetails}
					setMember={setDependentDetails}
				/>
			</Suspense>
			<div className="dependent-profile-information-container">
				{dependentProfileInformation !== undefined ? (
					<DynamicInformationForm informationFormData={dependentProfileInformation} />
				) : null}
			</div>
			<Suspense fallback={<div />}>
				<ThemeFormSubHeader title={"Contact Information"} />
			</Suspense>
			<div>
				{dependentContactInformation !== undefined ? (
					<DynamicInformationForm informationFormData={dependentContactInformation} />
				) : null}
			</div>
		</>
	);
};

export default DependentInformation;
